/**
 * Settings — ekran ustawień tenanta.
 *
 * Layout (od F12.A T12.A5): pionowe sub-menu po lewej + content po prawej (zamiast tab bar).
 * Wzorzec z SellCall pokazany przez Łukasza w rozmowie 2026-05-07.
 *
 * Grupy w sub-menu (kolejność = priorytet biznesowy, po F12.M T12.M.C.5/C.6):
 *   1. Konto              — Profil (link do /profile)
 *   2. Ogólne             — Uprawnienia użytkowników, Szablony ról
 *   3. Pakiety            — Pakiety modułów subskrypcji (F8 ModulesTab, rename z „Moduły systemowe")
 *   4. Moduły             — Moduły systemowe (link do /system-modules, F12.M Etap C),
 *                            Moduły własne (link do /modules, F12.A), Pola modułów (F12.D.9)
 *   5. Rozliczenia        — Faktury i płatności (super-user only)
 *
 * Usunięte z sidebar po F12.M C.6:
 *   - Grupa „Kontrahenci → Kategorie kontrahentów" — drzewko F11 (`contractor_categories`),
 *     wraz z całym mechanizmem usunięte; zastąpione grupami ze słownika `contractor_groups`.
 *
 * Deep-link zachowany: `?tab=<id>` — same wartości co przed refactorem (BC).
 *
 * NEG scope T12.A5:
 *   - Brak nowych komponentów per tab — używamy tych samych co przed.
 *   - "Pola modułów" (osobna podstrona z agregatem pól) — deferred do T12.A7+.
 *   - Customizacja layoutu sub-menu (drag&drop, hide groups) — nie na MVP.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftRight,
  BookOpenText,
  Boxes,
  CalendarOff,
  ClipboardList,
  CreditCard,
  Database,
  FileCheck,
  FileStack,
  FileType,
  Landmark,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  ListChecks,
  ListTree,
  type LucideIcon,
  Menu,
  Network,
  PanelLeft,
  Search,
  ShieldCheck,
  Tags,
  User,
  Workflow,
  Zap,
} from 'lucide-react';

import { Input } from '@/components/Input';
import { ColumnResizer } from '@/components/menu/ColumnControls';
import { AdditionalTablesTab } from '@/components/settings/AdditionalTablesTab';
import { AdditionalViewsTab } from '@/components/settings/AdditionalViewsTab';
import { BillingTab } from '@/components/settings/BillingTab';
import { ChartOfAccountsTab } from '@/components/settings/ChartOfAccountsTab';
import { DecreeProceduresTab } from '@/components/settings/DecreeProceduresTab';
import { DecreeTemplatesTab } from '@/components/settings/DecreeTemplatesTab';
import { DictionariesTab } from '@/components/settings/DictionariesTab';
import { DocumentTypesTab } from '@/components/settings/DocumentTypesTab';
import { ErpIntegrationTabs } from '@/components/settings/ErpIntegrationTabs';
import { KsefSettingsTab } from '@/components/settings/KsefSettingsTab';
import { LEGACY_SYNC_VIEW, LegacySyncTab } from '@/components/settings/LegacySyncTab';
import { ListActionsSettingsTab } from '@/components/settings/ListActionsSettingsTab';
import { ModuleFieldsTab } from '@/components/settings/ModuleFieldsTab';
import { ModulesOverviewTab } from '@/components/settings/ModulesOverviewTab';
import { ModulesTab } from '@/components/settings/ModulesTab';
import { NonWorkingDaysTab } from '@/components/settings/NonWorkingDaysTab';
import { OrgStructureTab } from '@/components/settings/OrgStructureTab';
import { PermissionCenterTab } from '@/components/settings/PermissionCenterTab';
import { RailSettingsTab } from '@/components/settings/RailSettingsTab';
import { RecordCategoriesSettingsTab } from '@/components/settings/RecordCategoriesSettingsTab';
import { RecordLinkTypesTab } from '@/components/settings/RecordLinkTypesTab';
import { SettingsNavScrollArea } from '@/components/settings/SettingsNavScrollArea';
import { SqlProceduresTab, type SqlProceduresTabId } from '@/components/settings/SqlProceduresTab';
import { TaskTypesTab } from '@/components/settings/TaskTypesTab';
import { UsersPermissionsTab } from '@/components/settings/UsersPermissionsTab';
import { WidgetsTab } from '@/components/settings/WidgetsTab';
import {
  SIDEBAR_WIDTH,
  SidebarCollapseProvider,
  SidebarCollapseToggle,
  SidebarItemLabel,
} from '@/components/sidebar-collapse';
import { usePermission } from '@/hooks/usePermission';
import { useSettingsSectionAccess } from '@/hooks/useSettingsSectionAccess';
import { MyProfile } from '@/pages/MyProfile';

const VALID_TABS = [
  'profile',
  // Osobisty konfigurator paska ikon (kolejność + widoczność) — każdy user po swojemu.
  'sidebar-rail',
  'modules',
  'permissions',
  // F24 Kawałek 1 — Centrum uprawnień (drzewo obiektów + trójstan). DOCHODZI obok dotychczasowych
  // zakładek, nie zastępuje ich: dostęp do firm i zakładanie ról zostają tam, gdzie były.
  'permission-center',
  // Szablony ról i zespoły przeniesione do Centrum uprawnień (F24) — ekrany usunięte, więc
  // wpisy zakładek zniknęły razem z nimi.
  'org-structure',
  'billing',
  // F12.A T12.A5 — nowy item, link do /modules
  'custom-modules',
  // F12.D T12.D.9 — globalny widok wszystkich pól modułów
  'module-fields',
  // F12.M T12.M.C.5 — link do /system-modules (5 modułów systemowych)
  'system-modules-link',
  // UX: połączony przegląd modułów (systemowe + własne) od razu w Ustawieniach
  'modules-overview',
  // F12.N T5.3 — słowniki tenanta (typy adresów, źródło kontrahenta itp.)
  'dictionaries',
  // Dni wolne od pracy (święta co roku + jednorazowe) — gated `crm_core.non_working_days.manage`
  // (`canManageNonWorkingDays`), BEZ modułu (crm_core zawsze aktywny).
  'non-working-days',
  // F.RL — słownik typów powiązań między kartotekami (kontrahent/pracownik/towar/dokument)
  'record-link-types',
  // F23 T1.7 — kreator widgetów danych pulpitu (definicje zapisane server-side, tenant-globalne)
  'widgets',
  // F23 Etap 2 — link do edytora domyślnego pulpitu tenanta (admin-only)
  'default-dashboard',
  // Wspólny panel osi „Kategoria" (record_categories) — per kartoteka; dziś kontrahenci i dokumenty
  'record-categories',
  // out-of-plan ADR-0031 — kreator menu sidebara per tenant
  'menu-builder',
  // F18 T18.1.a — integracje ERP (Symfonia w MVP, generic schema dla kolejnych dostawców)
  'integrations',
  // KSeF — Krajowy System e-Faktur (konfiguracja połączeń per firma)
  'ksef',
  // ADR-0110 — synchronizacja ze starym WF: stan i rozstrzyganie rozbieżności. Konfiguracja
  // i uruchamianie przebiegów zostają w panelu ITE; tutaj jest to, o czym decyduje klient.
  'legacy-sync',
  // F20 T20.17 — typy dokumentów (słownik per klasa: faktury, umowy, pisma, handlowe, wewnętrzne)
  'document-types',
  // Typy zadań (słownik typów kartoteki Zadania)
  'task-types',
  // Plan kont (fundament dekretacji faktury) — drzewo kont per firma
  'chart-of-accounts',
  // Szablony dekretacji ("Podpowiedz dekret") — wzorce podziału Wn/Ma per firma + klasa
  'decree-templates',
  // Procedury dekretacji ("Uruchom procedurę dekretacji") — autorski SQL per firma (opcjonalna) + klasa
  'decree-procedures',
  // F12.S T12.S.C.5 — zarządzanie definicjami tabel dodatkowych
  'additional-tables',
  // F22 — katalog widoków dodatkowych (sekcje SQL read-only na formatce kartoteki)
  'additional-views',
  // link do definicji procesów (redirect /processes) — widoczny tylko dla
  // superadmina tenanta z aktywnym modułem `processes`
  'process-definitions',
  // „Procedury SQL" — jedna sekcja z zakładkami (`?tab=records|processes`): procedury cyklu
  // życia kartotek (gated `useCanManageRecordProcedures()`, BEZ modułu) + procedury kontrolne
  // przejść procesu (ADR-0074, gated `useCanManageProcessProcedures()`). Stare sekcje
  // `record-procedures` / `process-procedures` przekierowują tu (`LEGACY_SECTION_REDIRECTS`).
  'sql-procedures',
  // Akcje list Faza 2 — własne akcje tenanta per kartoteka (SQL/webhook/powiązanie/dekret);
  // gated super-user (admin tenanta). Analogiczny ekran w panelu ITE dojdzie później.
  'list-actions',
] as const;
type TabId = (typeof VALID_TABS)[number];

function isValidTab(value: string | null): value is TabId {
  return !!value && (VALID_TABS as readonly string[]).includes(value);
}

/**
 * Dawne sekcje (zakładki, deep-linki, wpisy w historii) → zakładka sekcji „Procedury SQL", na którą
 * prowadzą. Przekierowanie nie czeka na sesję — o widoczności decyduje dopiero sekcja docelowa.
 */
const LEGACY_SECTION_REDIRECTS: ReadonlyMap<string, SqlProceduresTabId> = new Map<
  string,
  SqlProceduresTabId
>([
  ['record-procedures', 'records'],
  ['process-procedures', 'processes'],
]);

interface MenuItem {
  id: TabId;
  labelKey: string;
  icon: LucideIcon;
  content: React.ReactNode;
  /** Nagłówek H1 nad treścią; domyślnie = etykieta zakładki. Override np. „Akcje na listach". */
  headerTitleKey?: string;
  /** Pozycja-link — klik nawiguje wprost do tej trasy (własna karta), zamiast /settings/<id>. */
  href?: string;
}

interface MenuGroup {
  labelKey: string;
  items: MenuItem[];
}

// ─── Tab "Profil" jako Link Card (BC z poprzednią wersją) ───────────────────

// „Profil" w Ustawieniach pokazuje pełny ekran profilu OD RAZU (embedded), bez pośredniej
// karty-linku i dodatkowego klikania (decyzja Eugenii, jak w SellCall). Ta sama treść co
// trasa /profile — jedno źródło, dwa wejścia (Ustawienia + prawy górny róg w topbarze).
function ProfileTab() {
  return <MyProfile embedded />;
}

// ─── Tab „Edytuj menu" (out-of-plan, ADR-0031) — od razu galeria menu ──
// Makieta v2 (2026-07-02): klik w zakładkę przenosi wprost do /menu-builder
// (lista menu modułowych + własnych), bez pośredniej karty-linku.

// Znacznik `replaceTab` — gdy ktoś wejdzie WPROST na `/settings/<link>` (deep-link/zakładka),
// powłoka zamienia tę przejściową kartę w miejscu na kartę celu, zamiast zostawić sierocą
// kartę „Ustawienia" obok. Zwykły klik z paska i tak omija ten redirect (onChange → item.href).
const REPLACE_TAB_STATE = { replaceTab: true } as const;

function MenuBuilderLinkTab() {
  return <Navigate to="/menu-builder" replace state={REPLACE_TAB_STATE} />;
}

// ─── Tab „Definicje procesów" — redirect wprost do /processes ──────────────

function ProcessDefinitionsLinkTab() {
  return <Navigate to="/processes" replace state={REPLACE_TAB_STATE} />;
}

// ─── Tab „Domyślny pulpit" — redirect wprost do edytora domyślnego pulpitu (F23 Etap 2) ──
function DefaultDashboardLinkTab() {
  return <Navigate to="/dashboard/default" replace state={REPLACE_TAB_STATE} />;
}

// ─── Zwijanie kolumny ustawień ────────────────────────────────────────────
// Niezależne od głównego sidebara (osobny klucz localStorage) — inaczej zwinięcie
// ustawień sklejałoby się ze zwinięciem menu aplikacji (te same ikony/tooltipy, ale
// osobny stan). Wzorzec zachowania (icon-rail + tooltip) reużyty z sidebar-collapse.
const SETTINGS_NAV_COLLAPSED_KEY = 'settings.nav.collapsed';

function useSettingsNavCollapsed(): { collapsed: boolean; toggle: () => void } {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SETTINGS_NAV_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SETTINGS_NAV_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* localStorage niedostępny (private mode) — stan tylko w pamięci. */
      }
      return next;
    });
  }, []);
  return { collapsed, toggle };
}

// #10 — szerokość kolumny ustawień, przesuwana jak główny sidebar, ale NIEZALEŻNie
// (osobny klucz). Granice i domyślna wspólne z sidebarem (SIDEBAR_WIDTH).
const SETTINGS_NAV_WIDTH_KEY = 'settings.nav.width';

function useSettingsNavWidth(): { widthPx: number; setWidthPx: (w: number) => void } {
  const [widthPx, setWidth] = useState<number>(() => {
    try {
      const n = Number(window.localStorage.getItem(SETTINGS_NAV_WIDTH_KEY));
      return Number.isFinite(n) && n >= SIDEBAR_WIDTH.min && n <= SIDEBAR_WIDTH.max
        ? n
        : SIDEBAR_WIDTH.default;
    } catch {
      return SIDEBAR_WIDTH.default;
    }
  });
  const setWidthPx = useCallback((w: number) => {
    const clamped = Math.min(SIDEBAR_WIDTH.max, Math.max(SIDEBAR_WIDTH.min, Math.round(w)));
    setWidth(clamped);
    try {
      window.localStorage.setItem(SETTINGS_NAV_WIDTH_KEY, String(clamped));
    } catch {
      /* localStorage niedostępny — szerokość tylko w pamięci. */
    }
  }, []);
  return { widthPx, setWidthPx };
}

// ─── Settings root ──────────────────────────────────────────────────────────

export function Settings() {
  const { t } = useTranslation();
  // Bramki sekcji — wspólne z globalną wyszukiwarką (Ctrl+K), patrz useSettingsSectionAccess.
  const {
    isSuperUser,
    canManageCustomModules,
    canManageAdditionalViews,
    canManageWidgets,
    canManageDefaultDashboard,
    canManageRecordCategories,
    canManageProcessDefinitions,
    canManageSqlProcedures,
    canManageNonWorkingDays,
    canManageListActions,
    sessionLoaded,
  } = useSettingsSectionAccess();
  // Synchronizacja ze starym systemem — widoczna tylko z prawem podglądu. Samo prawo nie
  // wystarcza, żeby sekcja miała sens: jeśli tenant nie ma instalacji, ekran mówi to wprost.
  const mozeWidziecSynchronizacje = usePermission(LEGACY_SYNC_VIEW).allowed;
  const { section } = useParams<{ section?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Sekcja z trasy /settings/<section>; samo /settings (bez parametru) → domyślnie profil.
  // Stare deep-linki ?tab= czytamy nadal (BC), gdy brak parametru trasy.
  const requested = section ?? searchParams.get('tab');
  const legacyTab = requested ? LEGACY_SECTION_REDIRECTS.get(requested) : undefined;
  const legacyRedirect = legacyTab ? `/settings/sql-procedures?tab=${legacyTab}` : undefined;
  // Dawna sekcja: do czasu przekierowania (efekt niżej) podświetlamy już sekcję docelową, nie
  // Profil; jej treść czeka na przekierowanie (render niżej), żeby nie zamontować na chwilę
  // pierwszej zakładki zamiast docelowej.
  const requestedTab: TabId = legacyRedirect
    ? 'sql-procedures'
    : isValidTab(requested)
      ? requested
      : 'profile';
  // Tab `billing` widoczny tylko dla super-userow tenanta — fallback do `profile`.
  const tab: TabId = requestedTab === 'billing' && !isSuperUser ? 'profile' : requestedTab;

  // Kontener przewijania treści zachowuje scrollTop między sekcjami — po kliknięciu pozycji
  // niżej w nawigacji (np. „Integracje ERP") nowa, krótsza sekcja pokazywała się przewinięta,
  // z ukrytym nagłówkiem. Reset scrolla na górę przy każdej zmianie sekcji.
  const contentRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0 });
  }, [tab]);

  const groups: MenuGroup[] = useMemo(() => {
    const list: MenuGroup[] = [
      {
        labelKey: 'tenant.settings.groups.account',
        items: [
          {
            id: 'profile',
            labelKey: 'tenant.settings.tabs.profile',
            icon: User,
            content: <ProfileTab />,
          },
          {
            id: 'sidebar-rail',
            labelKey: 'tenant.settings.tabs.sidebarRail',
            icon: PanelLeft,
            content: <RailSettingsTab />,
          },
        ],
      },
      {
        labelKey: 'tenant.settings.groups.general',
        items: [
          {
            id: 'permissions',
            labelKey: 'tenant.settings.tabs.permissions',
            icon: ShieldCheck,
            content: <UsersPermissionsTab />,
          },
          {
            id: 'permission-center',
            labelKey: 'tenant.settings.tabs.permissionCenter',
            icon: ShieldCheck,
            content: <PermissionCenterTab />,
          },
          {
            id: 'org-structure',
            labelKey: 'tenant.settings.tabs.orgStructure',
            icon: Network,
            content: <OrgStructureTab />,
          },
          {
            id: 'dictionaries',
            labelKey: 'tenant.settings.tabs.dictionaries',
            icon: BookOpenText,
            content: <DictionariesTab />,
          },
          // Dni wolne od pracy — gated: crm_core.non_working_days.manage (crm_core, BEZ modułu).
          ...(canManageNonWorkingDays
            ? [
                {
                  id: 'non-working-days' as const,
                  labelKey: 'tenant.settings.tabs.nonWorkingDays',
                  icon: CalendarOff,
                  content: <NonWorkingDaysTab />,
                },
              ]
            : []),
          {
            id: 'record-link-types',
            labelKey: 'tenant.settings.tabs.recordLinkTypes',
            icon: Link2,
            content: <RecordLinkTypesTab />,
          },
          // F23 T1.7 — kreator widgetów danych pulpitu, gated: crm_core.widgets.manage.
          ...(canManageWidgets
            ? [
                {
                  id: 'widgets' as const,
                  labelKey: 'tenant.settings.tabs.widgets',
                  icon: LayoutDashboard,
                  content: <WidgetsTab />,
                },
              ]
            : []),
          // F23 Etap 2 — link-karta „Domyślny pulpit", gated: crm_core.dashboard.manage_default.
          ...(canManageDefaultDashboard
            ? [
                {
                  id: 'default-dashboard' as const,
                  labelKey: 'tenant.settings.tabs.defaultDashboard',
                  icon: LayoutTemplate,
                  href: '/dashboard/default',
                  content: <DefaultDashboardLinkTab />,
                },
              ]
            : []),
          {
            id: 'document-types',
            labelKey: 'tenant.settings.tabs.documentTypes',
            icon: FileType,
            content: <DocumentTypesTab />,
          },
          {
            id: 'task-types',
            labelKey: 'tenant.settings.tabs.taskTypes',
            icon: ClipboardList,
            content: <TaskTypesTab />,
          },
          // Wspólny panel osi „Kategoria" (record_categories) — per kartoteka. Gated:
          // crm_core.system_modules.manage (spójnie z mutacjami backendu) + super-user.
          ...(canManageRecordCategories
            ? [
                {
                  id: 'record-categories' as const,
                  labelKey: 'tenant.settings.tabs.recordCategories',
                  icon: Tags,
                  content: <RecordCategoriesSettingsTab />,
                },
              ]
            : []),
          {
            id: 'menu-builder',
            labelKey: 'tenant.settings.tabs.menuBuilder',
            icon: Menu,
            href: '/menu-builder',
            content: <MenuBuilderLinkTab />,
          },
          // Link do definicji procesów — gated: useCanManageProcessDefinitions().
          ...(canManageProcessDefinitions
            ? [
                {
                  id: 'process-definitions' as const,
                  labelKey: 'tenant.settings.tabs.processDefinitions',
                  icon: Workflow,
                  href: '/processes',
                  content: <ProcessDefinitionsLinkTab />,
                },
              ]
            : []),
          // „Procedury SQL" — zakładki: procedury kartotek (useCanManageRecordProcedures, crm_core,
          // BEZ modułu) i procedury procesów (ADR-0074, useCanManageProcessProcedures). Pozycja
          // widoczna przy którymkolwiek z praw; zakładki bramkuje sam SqlProceduresTab.
          ...(canManageSqlProcedures
            ? [
                {
                  id: 'sql-procedures' as const,
                  labelKey: 'tenant.settings.tabs.sqlProcedures',
                  icon: ListChecks,
                  content: <SqlProceduresTab />,
                },
              ]
            : []),
          // Akcje list Faza 2 — własne akcje tenanta per kartoteka. Autorstwo bramkowane
          // (dziś super-user/admin tenanta; docelowe uprawnienie + ITE dojdą przy backendzie).
          ...(canManageListActions
            ? [
                {
                  id: 'list-actions' as const,
                  labelKey: 'tenant.settings.tabs.listActions',
                  headerTitleKey: 'tenant.listActions.settings.pageTitle',
                  icon: Zap,
                  content: <ListActionsSettingsTab />,
                },
              ]
            : []),
        ],
      },
      // Księgowość — fundament dekretacji faktury (plan kont per firma).
      {
        labelKey: 'tenant.settings.groups.accounting',
        items: [
          {
            id: 'chart-of-accounts',
            labelKey: 'tenant.settings.tabs.chartOfAccounts',
            icon: Landmark,
            content: <ChartOfAccountsTab />,
          },
          {
            id: 'decree-templates',
            labelKey: 'tenant.settings.tabs.decreeTemplates',
            icon: FileStack,
            content: <DecreeTemplatesTab />,
          },
          {
            id: 'decree-procedures',
            labelKey: 'decreeProcedures.tabLabel',
            icon: FileStack,
            content: <DecreeProceduresTab />,
          },
        ],
      },
      // F12.M T12.M.C.5: F8 „Moduły systemowe" → przeniesione do grupy „Pakiety"
      // pod swoją własną nazwą. Nowa grupa „Moduły" agreguje moduły własne +
      // systemowe (5 encji CRM core) + globalny widok pól.
      {
        labelKey: 'tenant.settings.groups.packages',
        items: [
          {
            id: 'modules',
            labelKey: 'tenant.settings.tabs.modules',
            icon: Boxes,
            content: <ModulesTab />,
          },
        ],
      },
      // F18 T18.1.a — integracje ERP (Symfonia MVP). Placeholder w T18.1.a, pełen CRUD UI w T18.1.b.
      {
        labelKey: 'tenant.settings.groups.integrations',
        items: [
          {
            id: 'integrations',
            labelKey: 'tenant.settings.tabs.integrations',
            icon: ArrowLeftRight,
            content: <ErpIntegrationTabs />,
          },
          {
            id: 'ksef',
            labelKey: 'tenant.settings.tabs.ksef',
            icon: FileCheck,
            content: <KsefSettingsTab />,
          },
          // Widoczna tylko dla tych, którzy mają prawo podglądu synchronizacji. Kto go nie ma,
          // nie zobaczy pozycji w ogóle — zakładka, w którą nie wolno wejść, jest gorsza niż
          // jej brak.
          ...(mozeWidziecSynchronizacje
            ? [
                {
                  id: 'legacy-sync' as const,
                  labelKey: 'tenant.settings.tabs.legacySync',
                  icon: ArrowLeftRight,
                  content: <LegacySyncTab />,
                },
              ]
            : []),
        ],
      },
      {
        labelKey: 'tenant.settings.groups.modules',
        items: [
          {
            id: 'modules-overview',
            labelKey: 'tenant.settings.tabs.modulesOverview',
            icon: Boxes,
            content: <ModulesOverviewTab />,
          },
          {
            id: 'module-fields',
            labelKey: 'tenant.settings.tabs.moduleFields',
            icon: ListTree,
            content: <ModuleFieldsTab />,
          },
          // F12.S T12.S.C.5 — tabele dodatkowe (gated: crm_core.custom_modules.manage)
          ...(canManageCustomModules
            ? [
                {
                  id: 'additional-tables' as const,
                  labelKey: 'additionalTables.admin.tabLabel',
                  icon: Database,
                  content: <AdditionalTablesTab />,
                },
              ]
            : []),
          // F22 — widoki dodatkowe (gated: crm_core.additional_views.manage, ADR-0087)
          ...(canManageAdditionalViews
            ? [
                {
                  id: 'additional-views' as const,
                  labelKey: 'additionalViews.admin.tabLabel',
                  icon: Database,
                  content: <AdditionalViewsTab />,
                },
              ]
            : []),
        ],
      },
      // F12.M T12.M.C.6: grupa „Kontrahenci → Kategorie kontrahentów" usunięta.
      // Drzewko F11 przeniesione do `/system-modules/contractors/settings?tab=categories`
      // (zakładka „Kategorie" w hub Kontrahenci). Legacy URL redirect w `Settings`.
    ];
    if (isSuperUser) {
      // Billing widoczne tylko dla super-userów. W E1 nie ma per-rola dostępu
      // do danych fakturowych platformy — flaga is_super_user wystarcza i jest
      // spójna z backendem (assertSuperUser w /api/auth/tenant-billing).
      list.push({
        labelKey: 'tenant.settings.groups.billing',
        items: [
          {
            id: 'billing',
            labelKey: 'tenant.settings.tabs.billing',
            icon: CreditCard,
            content: <BillingTab />,
          },
        ],
      });
    }
    return list;
  }, [
    isSuperUser,
    canManageCustomModules,
    canManageWidgets,
    canManageRecordCategories,
    canManageProcessDefinitions,
    canManageSqlProcedures,
    canManageNonWorkingDays,
    canManageListActions,
    mozeWidziecSynchronizacje,
  ]);

  const allItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const activeItem = allItems.find((i) => i.id === tab) ?? allItems[0];

  // Sekcja z trasy /settings/<section>, która realnie NIE jest renderowana (ukryta uprawnieniem
  // np. `billing`/`widgets`, albo nieznana) — treść spada na Profil, ale tytuł karty liczony jest
  // ze ścieżki i pokazywałby nazwę tej sekcji (mylące „Faktury i płatności" nad Profilem). Zjeżdżamy
  // wtedy na lądowanie /settings (karta „Ustawienia" = Profil), znacznikiem zamiany karty w miejscu,
  // żeby nie zostawić sieroty. Nie dotyczy braku parametru (samo /settings) ani „profile".
  useEffect(() => {
    // Dawna sekcja → jej nowe miejsce (bez czekania na sesję; widoczność ocenia sekcja docelowa).
    if (legacyRedirect) {
      navigate(legacyRedirect, { replace: true, state: { replaceTab: true } });
      return;
    }
    // Dopóki sesja (`me`) się nie wczyta, bramki `canManage*` są `false` i sekcje bramkowane
    // uprawnieniem nie ma jeszcze w menu — bez tej osłony deep-link/F5 na takiej zakładce
    // lądowałby na /settings, zanim uprawnienia zdążą dotrzeć.
    // Kompromis: kontekst auth zamienia KAŻDY błąd `/auth/me` na `null` (401 obsługuje interceptor
    // przekierowaniem na logowanie), więc po innym błędzie (np. 500, brak sieci) `me` zostaje `null`
    // na stałe i to przekierowanie nigdy nie odpali — na adresie nieznanej/ukrytej sekcji użytkownik
    // zobaczy wtedy Profil pod adresem tej sekcji (zamiast lądowania /settings). Świadomie: bez
    // zmian w kontekście auth, a bez sesji i tak nie ma czego pokazać ani czego chronić.
    if (!sessionLoaded) return;
    if (section && activeItem?.id !== section) {
      navigate('/settings', { replace: true, state: { replaceTab: true } });
    }
  }, [legacyRedirect, sessionLoaded, section, activeItem?.id, navigate]);

  // #8 — zwijanie kolumny (niezależne od głównego menu). #6b — lupka filtrująca pozycje.
  const { collapsed, toggle: toggleNav } = useSettingsNavCollapsed();
  const { widthPx, setWidthPx } = useSettingsNavWidth();
  const [navQuery, setNavQuery] = useState('');
  const navQ = navQuery.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    if (!navQ) return groups;
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => t(i.labelKey).toLowerCase().includes(navQ)),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, navQ, t]);

  // Każda sekcja Ustawień to OSOBNA karta powłoki (/settings/<id>) — nawigujemy PUSH, żeby
  // powłoka otworzyła/aktywowała kartę i trzymała ją żywą (keep-alive; koniec przeładowań
  // przy przełączaniu). Profil = strona-lądowanie /settings (karta „Ustawienia"). Pozycje-linki
  // (item.href, np. Kreator menu / Definicje procesów) prowadzą wprost do swojej trasy.
  const onChange = (item: MenuItem) => {
    if (item.href) {
      navigate(item.href);
      return;
    }
    navigate(item.id === 'profile' ? '/settings' : `/settings/${item.id}`);
  };

  // `h-full min-h-0` (nie `min-h-full`) — ekran Ustawien jest OGRANICZONY wysokoscia karty,
  // a nie rosnie z trescia. Dzieki temu tresc zakladki moze dostac wlasny, niezalezny scroll
  // pionowy i utrzymac poziomy pasek przewijania w widoku (tabela slownikow wychodzila
  // wczesniej za strone razem z tym paskiem). Panel karty jest `absolute inset-0`, wiec daje
  // tu definitywna wysokosc do podzialu.
  return (
    <main className="flex h-full min-h-0 flex-col bg-bg-secondary" data-testid="settings">
      {/* Pełnoszerokościowy nagłówek strony (redesign „Ant"): stały tytuł „Ustawienia”
          + podtytuł nad podziałem sub-nawigacja/treść. Wcześniej H1 był per-zakładka
          w kolumnie treści; teraz treść zaczyna się od własnego nagłówka sekcji. */}
      <header className="shrink-0 px-6 pb-5 pt-6" data-testid="settings-page-header">
        {/* Podtytuł „Zarządzaj swoim kontem i modułami firmy." usunięty (decyzja Eugenii 2026-09-16). */}
        <h1 className="text-2xl font-bold text-text-primary">{t('tenant.settings.title')}</h1>
      </header>

      {/* Sub-nawigacja i treść jako DWIE osobne pływające zaokrąglone karty (decyzja Eugenii
          2026-09-16) — zamiast jednego płaskiego panelu z pionową kreską. Odstęp między nimi
          (gap) + margines dołu/boków na miękkim tle sekcji. */}
      <div className="flex min-h-0 flex-1 gap-4 px-6 pb-6">
        {/* Sub-nawigacja — osobna karta.
            #8: zwijalna (icon-rail + tooltipy) jak główny sidebar, niezależnym stanem.
            #6b: lupka filtrująca pozycje (widoczna tylko gdy rozwinięta). */}
        <SidebarCollapseProvider collapsed={collapsed}>
          <aside
            className={
              (collapsed ? 'w-14 ' : '') +
              // `flex h-full min-h-0 flex-col` — pasek dostaje pełną (ograniczoną) wysokość karty,
              // a nagłówek + wyszukiwarka zostają na górze; przewija się sama lista (nav niżej).
              // Bez tego długa lista pozycji urywała się na dole, bez scrolla.
              'relative flex h-full min-h-0 shrink-0 flex-col rounded-2xl border border-border-secondary bg-bg-elevated px-3 py-6 shadow-sm'
            }
            style={collapsed ? undefined : { width: widthPx }}
            data-testid="settings-sidebar"
            data-collapsed={collapsed ? 'true' : 'false'}
          >
            {/* Przełącznik zwinięcia — kółko na prawej krawędzi (spójnie z głównym sidebarem). */}
            <SidebarCollapseToggle
              collapsed={collapsed}
              onToggle={toggleNav}
              testId="settings-nav-collapse-toggle"
            />
            {/* Górny pasek na kółko przełącznika — inaczej nachodziłoby na wyszukiwarkę/pierwszą
                pozycję (nav Ustawień nie ma nagłówka z logo jak główny sidebar). */}
            <div className="h-6 shrink-0" aria-hidden="true" />

            {!collapsed && (
              <div className="mb-4">
                <Input
                  type="search"
                  value={navQuery}
                  onChange={(e) => setNavQuery(e.target.value)}
                  placeholder={t('tenant.settings.searchPlaceholder')}
                  aria-label={t('tenant.settings.searchPlaceholder')}
                  prefixIcon={<Search className="h-4 w-4" aria-hidden="true" />}
                  data-testid="settings-nav-search"
                />
              </div>
            )}

            {/* Lista bez systemowego suwaka + strzałki góra/dół (jak pasek obszarów w sidebarze). */}
            <SettingsNavScrollArea ariaLabel={t('tenant.settings.title')}>
              {visibleGroups.map((group) => (
                <div key={group.labelKey}>
                  {!collapsed && (
                    <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
                      {t(group.labelKey)}
                    </div>
                  )}
                  <ul className="space-y-1">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = item.id === tab;
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => onChange(item)}
                            aria-current={isActive ? 'page' : undefined}
                            data-testid={`settings-nav-${item.id}`}
                            className={
                              'flex w-full items-center rounded-md text-sm transition ' +
                              (collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2') +
                              ' ' +
                              (isActive
                                ? 'bg-accent-500/10 font-medium text-accent-500'
                                : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary')
                            }
                          >
                            <Icon
                              className={
                                (collapsed ? 'h-5 w-5' : 'h-4 w-4') +
                                ' shrink-0 ' +
                                (isActive ? 'text-accent-500' : 'text-text-muted')
                              }
                              aria-hidden="true"
                            />
                            <SidebarItemLabel>{t(item.labelKey)}</SidebarItemLabel>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {!collapsed && navQ && visibleGroups.length === 0 && (
                <p className="px-3 text-sm text-text-muted" data-testid="settings-nav-empty">
                  {t('tenant.settings.searchNoResults')}
                </p>
              )}
            </SettingsNavScrollArea>
          </aside>
          {!collapsed && (
            <ColumnResizer
              width={widthPx}
              onWidthChange={setWidthPx}
              min={SIDEBAR_WIDTH.min}
              max={SIDEBAR_WIDTH.max}
            />
          )}
        </SidebarCollapseProvider>

        {/* Treść — osobna karta, zajmuje resztę szerokości */}
        <section
          ref={contentRef}
          className="flex min-h-0 flex-1 flex-col overflow-auto rounded-2xl border border-border-secondary bg-bg-elevated p-6 shadow-sm"
          data-testid="settings-content"
        >
          {/* Nagłówek sekcji = tytuł aktywnej zakładki. Pomijany dla „Profil”, bo osadzony
              MyProfile renderuje własny nagłówek „Mój profil” — inaczej byłby dublet pod
              pełnoszerokościowym „Ustawienia”. */}
          {activeItem && activeItem.id !== 'profile' && (
            <header className="mb-6">
              <h1 className="text-xl font-semibold text-text-primary">
                {t(activeItem.headerTitleKey ?? activeItem.labelKey)}
              </h1>
            </header>
          )}
          {/* `min-h-0 flex-1` — zakładka, która chce własnego scrolla (np. Słowniki: dwa
              niezależnie przewijane panele), dostaje tu definitywną wysokość i używa `h-full`.
              Zakładki bez takiego layoutu zachowują się jak dotąd — rosną i przewijają się
              scrollem sekcji. */}
          <div className="flex min-h-0 flex-1 flex-col" data-testid="settings-tab-content">
            {/* Dawna sekcja — treść dopiero po przekierowaniu na właściwą zakładkę (`legacyRedirect`). */}
            {activeItem && !legacyRedirect ? activeItem.content : null}
          </div>
        </section>
      </div>
    </main>
  );
}
